import React, { useState, useEffect } from 'react';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import {
  Badge,
  DescriptionInput,
  FieldGroup,
  FieldHint,
  FieldLabel,
  FormCard,
  Heading,
  Inner,
  LimitCard,
  LimitText,
  LimitTitle,
  LoadingCover,
  Panel,
  QuotaPill,
  SecondaryButton,
  Subheading,
  SubmitButton,
  TradeGrid,
  TradeOption,
} from './ServiceFinderAI.styled';
import { getNavigateToLoginOptions } from '../../utils/authSessionExpired';
import { useAuth } from '../../context/AuthContext';
import { aiService } from '../../services/api/aiService';
import NkySnackbarAlert from '../common/NkySnackbarAlert/NkySnackbarAlert';
import NkySpinner from '../common/NkySpinner/NkySpinner';
import { getTradePickerLabel, getTradeThemeList } from '../../constants/tradeTheme';

const DAILY_LIMIT = 2;
const ADMIN_ROLE = 'admin_user';
const TWENTY_FOUR_HOURS_MS = 24 * 60 * 60 * 1000;
const DESCRIPTION_MAX = 150;

const trades = getTradeThemeList();

const ServiceFinderAI = () => {
  const [formData, setFormData] = useState({ category: '', description: '' });
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Auth gate
  useEffect(() => {
    if (loading) return;
    if (!user) navigate('/login', getNavigateToLoginOptions(location));
  }, [user, loading, navigate, location]);

  // Optional prefill via navigation state
  const prefillCategory = location.state?.prefillCategory;
  useEffect(() => {
    if (!prefillCategory) return;
    const isValid = trades.some((t) => t.id === prefillCategory);
    if (!isValid) return;
    setFormData((prev) => (prev.category ? prev : { ...prev, category: prefillCategory }));
  }, [prefillCategory]);

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, severity: 'info', message: '' });

  const [usageLoading, setUsageLoading] = useState(true);
  const [usedCount, setUsedCount] = useState(0);
  const isAdmin = user?.Role === ADMIN_ROLE;

  const countRecentSearches = (history) => {
    if (!Array.isArray(history)) return 0;
    const now = Date.now();
    return history.filter((record) => {
      if (!record?.CreatedAt) return false;
      const createdAtMs = new Date(record.CreatedAt).getTime();
      if (Number.isNaN(createdAtMs)) return false;
      return now - createdAtMs <= TWENTY_FOUR_HOURS_MS;
    }).length;
  };

  useEffect(() => {
    if (loading || !user) return;
    if (isAdmin) {
      setUsageLoading(false);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        setUsageLoading(true);
        const history = await aiService.getRecommendationHistory(user.AccountId);
        if (cancelled) return;
        const count = countRecentSearches(history);
        setUsedCount(Math.min(count, DAILY_LIMIT));
      } catch (err) {
        if (!cancelled) {
          openSnack('error', 'Could not load your recent search history.');
          setUsedCount(0);
        }
      } finally {
        if (!cancelled) setUsageLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user, loading, isAdmin]);

  const remaining = Math.max(DAILY_LIMIT - usedCount, 0);
  const limitReached = !isAdmin && remaining === 0;

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleTradeSelect = (category) => {
    setFormData((prev) => ({ ...prev, category }));
    setErrors((prev) => ({ ...prev, category: undefined }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.category) newErrors.category = 'Pick the kind of pro you need.';
    if (!formData.description.trim()) {
      newErrors.description = 'Tell us a little about the job.';
    } else if (formData.description.length > DESCRIPTION_MAX) {
      newErrors.description = `Maximum ${DESCRIPTION_MAX} characters allowed.`;
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const openSnack = (severity, message) =>
    setSnackbar({ open: true, severity, message });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (limitReached) return;
    if (!validateForm()) return;

    setIsLoading(true);

    const ServiceType = formData.category;
    const CustomerDemand = formData.description.trim();

    try {
      const { status, data } = await aiService.getAiRecommendations(ServiceType, CustomerDemand);

      if (status === 201) {
        if (!isAdmin) {
          setUsedCount((c) => Math.min(c + 1, DAILY_LIMIT));
        }

        navigate('/ai-service-recommendations', {
          state: {
            recommendations: (data && data.recommendations) || [],
            request: { ServiceType, CustomerDemand },
            createdAt: Date.now()
          }
        });
        setFormData({ category: '', description: '' });
        setErrors({});
      } else if (status === 401) {
        navigate('/login', getNavigateToLoginOptions(location));
      } else if (status === 404) {
        openSnack('info', 'No services found for the selected type.');
      } else if (status === 429) {
        setUsedCount(DAILY_LIMIT);
        openSnack('info', 'You\u2019ve used your two free searches today. Please try again tomorrow.');
      } else if (status === 500) {
        openSnack('error', 'Something went wrong. Please try again later.');
      } else {
        openSnack('error', 'Unexpected response from the server.');
      }
    } catch (err) {
      openSnack('error', 'Unexpected error. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const showInitialSpinner = loading || (user && usageLoading);

  const selectedTrade = trades.find((t) => t.id === formData.category);

  return (
    <Panel
      aria-labelledby="ai-search-title"
      aria-busy={isLoading || showInitialSpinner}
      style={selectedTrade ? { '--trade': selectedTrade.color } : undefined}
    >
      {(isLoading || showInitialSpinner) && (
        <LoadingCover>
          <NkySpinner size="lg" />
        </LoadingCover>
      )}

      <Inner>
        <Badge>
          <AutoAwesomeRoundedIcon aria-hidden />
          Smart search
        </Badge>
        <Heading id="ai-search-title">
          Describe the job. We&apos;ll find the <em>right pro</em>.
        </Heading>
        <Subheading>
          Tell us what you need help with in your own words. We&apos;ll match you with the best
          local providers in Northern Kentucky.
        </Subheading>

        {!showInitialSpinner && !isAdmin && !limitReached && (
          <QuotaPill>
            <span>
              <strong>{remaining}</strong> of <strong>{DAILY_LIMIT}</strong> smart searches left
              today
            </span>
          </QuotaPill>
        )}

        {!showInitialSpinner && limitReached ? (
          <LimitCard>
            <LimitTitle>You&apos;re out of smart searches for today</LimitTitle>
            <LimitText>
              You&apos;ve used both free smart searches in the last 24 hours. Your limit resets
              automatically, or you can use the guided search now.
            </LimitText>
            <SecondaryButton component={RouterLink} to="/find-a-pro" variant="outlined">
              Use the guided search
            </SecondaryButton>
          </LimitCard>
        ) : (
          !showInitialSpinner && (
            <FormCard onSubmit={handleSubmit} noValidate>
              <FieldGroup role="group" aria-labelledby="ai-trade-label">
                <FieldLabel as="p" id="ai-trade-label">
                  What kind of pro do you need?
                </FieldLabel>
                <TradeGrid>
                  {trades.map(({ id, icon: Icon, color }) => (
                    <TradeOption
                      key={id}
                      type="button"
                      $color={color}
                      aria-pressed={formData.category === id}
                      onClick={() => handleTradeSelect(id)}
                    >
                      <Icon aria-hidden />
                      {getTradePickerLabel(id)}
                    </TradeOption>
                  ))}
                </TradeGrid>
                {errors.category && <FieldHint $error>{errors.category}</FieldHint>}
              </FieldGroup>

              <FieldGroup>
                <FieldLabel htmlFor="ai-description">What do you need help with?</FieldLabel>
                <DescriptionInput
                  id="ai-description"
                  name="description"
                  placeholder={`e.g., ${getPlaceholder(formData.category)}`}
                  value={formData.description}
                  onChange={handleChange}
                  maxLength={DESCRIPTION_MAX}
                  aria-invalid={Boolean(errors.description)}
                  aria-describedby="ai-description-hint"
                />
                <FieldHint id="ai-description-hint" $error={Boolean(errors.description)}>
                  {errors.description ||
                    `${formData.description.length}/${DESCRIPTION_MAX} characters`}
                </FieldHint>
              </FieldGroup>

              <SubmitButton type="submit" disabled={isLoading}>
                Find my pro
              </SubmitButton>
            </FormCard>
          )
        )}
      </Inner>

      <NkySnackbarAlert
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        severity={snackbar.severity}
        message={snackbar.message}
      />
    </Panel>
  );
};

const getPlaceholder = (category) => {
  switch (category) {
    case 'electrical':
      return 'A few outlets in my kitchen stopped working and the breaker keeps tripping.';
    case 'hvac':
      return 'The air conditioner is running but the house still feels hot.';
    case 'landscaping':
      return 'I need someone to mow the front and back yard and trim the bushes.';
    case 'plumbing':
      return 'My kitchen faucet won\'t stop dripping and it\'s getting worse.';
    default:
      return "A few things around the house need fixing and I'm not sure who to call.";
  }
};

export default ServiceFinderAI;